import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FileText, 
  ClipboardList, 
  Award, 
  Users, 
  UserCheck,
  TrendingUp
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Loader from '../../components/ui/Loader';
import procesoService from '../../services/procesoService';
import perfilService from '../../services/perfilService';
import examenService from '../../services/examenService';
import usuarioService from '../../services/usuarioService';
import usuarioProcesoService from '../../services/usuarioProcesoService';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProcesos: 0,
    totalPerfiles: 0,
    totalExamenes: 0,
    totalUsuarios: 0,
    totalAsignaciones: 0,
    asignacionesActivas: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      
      // Cargar procesos
      let totalProcesos = 0;
      try {
        const procesos = await procesoService.getAllProcesos();
        totalProcesos = procesos?.length || 0;
      } catch (err) {
        console.error('Error loading procesos:', err);
      }

      // Cargar perfiles
      let totalPerfiles = 0;
      try {
        const perfiles = await perfilService.getAllPerfiles();
        totalPerfiles = perfiles?.length || 0;
      } catch (err) {
        console.error('Error loading perfiles:', err);
      }

      // Cargar exámenes
      let totalExamenes = 0;
      try {
        const examenes = await examenService.getAllExamenes();
        totalExamenes = examenes?.length || 0;
      } catch (err) {
        console.error('Error loading examenes:', err);
      }

      // Cargar usuarios
      let totalUsuarios = 0;
      try {
        const usuarios = await usuarioService.getAllUsuarios();
        totalUsuarios = usuarios?.length || 0;
      } catch (err) {
        console.error('Error loading usuarios:', err);
      }

      // Cargar asignaciones
      let totalAsignaciones = 0;
      let asignacionesActivas = 0;
      try {
        const asignaciones = await usuarioProcesoService.getAllUsuarioProcesos();
        totalAsignaciones = asignaciones?.length || 0;
        asignacionesActivas = asignaciones?.filter(
          a => a.estado === 'ASIGNADO' || a.estado === 'EN_PROCESO'
        ).length || 0;
      } catch (err) {
        console.error('Error loading asignaciones:', err);
      }

      setStats({
        totalProcesos,
        totalPerfiles,
        totalExamenes,
        totalUsuarios,
        totalAsignaciones,
        asignacionesActivas
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total Procesos',
      value: stats.totalProcesos,
      icon: FileText,
      color: 'bg-[#fe7f2d]',
      link: '/supervisor/procesos'
    },
    {
      title: 'Total Perfiles',
      value: stats.totalPerfiles,
      icon: ClipboardList,
      color: 'bg-[#297373]',
      link: '/supervisor/perfiles'
    },
    {
      title: 'Total Exámenes',
      value: stats.totalExamenes,
      icon: Award,
      color: 'bg-blue-500',
      link: '/supervisor/examenes'
    },
    {
      title: 'Total Usuarios',
      value: stats.totalUsuarios,
      icon: Users,
      color: 'bg-purple-500',
      link: '/supervisor/usuarios'
    },
    {
      title: 'Total Asignaciones',
      value: stats.totalAsignaciones,
      icon: UserCheck,
      color: 'bg-indigo-500',
      link: '/supervisor/asignaciones'
    },
    {
      title: 'Asignaciones Activas',
      value: stats.asignacionesActivas,
      icon: TrendingUp,
      color: 'bg-green-500',
      link: '/supervisor/asignaciones'
    }
  ];

  const quickAccessButtons = [
    { title: 'Gestionar Procesos', link: '/supervisor/procesos', icon: FileText },
    { title: 'Gestionar Usuarios', link: '/supervisor/usuarios', icon: Users }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="text-3xl font-bold text-neutral-800 mb-2">
          Panel de Supervisor
        </h1>
        <p className="text-neutral-600">
          Bienvenido al sistema de gestión de procesos de selección
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statsCards.map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card 
              onClick={() => navigate(stat.link)}
              className="cursor-pointer hover:shadow-lg transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-neutral-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-neutral-800 mt-2">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <h2 className="text-xl font-semibold text-neutral-800 mb-4">
          Acceso Rápido
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickAccessButtons.map((button, index) => (
            <Card
              key={index}
              onClick={() => navigate(button.link)}
              className="cursor-pointer hover:shadow-lg transition-shadow text-center"
            >
              <button.icon className="w-8 h-8 text-[#fe7f2d] mx-auto mb-2" />
              <p className="text-sm font-medium text-neutral-700">{button.title}</p>
            </Card>
          ))}
        </div>
      </motion.div>

      
    </div>
  );
};

export default Dashboard;
